import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { userRoles } from 'constants/users';
import { categories } from 'constants/tests';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { useFreecodeContextData } from 'hooks/useContext';
import { RectangleButton } from 'components/Button/RectangleButton';
import FreecodeVersioning from './FreecodeVersioning';
import FreecodeTestMeta from './FreecodeTestMeta';
import FreecodeLevelSettings from './FreecodeLevelSettings';
import MoveTestToCompanyModal from './Modals/MoveTestToCompanyModal';
import FreecodeFlowOverlay from 'components/FreecodePage/FreecodeFlowOverlay';
import {
  CreatorTabContentBackground,
  CreatorTabRowContainer,
  CreatorTabColumnContainer,
  CreatorSection,
  NumberInput,
  SectionHeader,
  Label,
  Text,
  InputContainer,
} from '../styles';

const OFFICIAL_TAG = 'Official';

export default function FreecodeSettings() {
  const { test, setTest, testImpl, setTestImpl, setShowFlowOverlay } = useFreecodeContextData();

  const { userRole } = useSelector((state) => state.auth);

  const [showMoveTestModal, setShowMoveTestModal] = useState(false);

  const handleConfigLevelsUpdate = (parameter, value) =>
    setTestImpl((oldTestImpl) => ({
      ...oldTestImpl,
      configLevels: { ...oldTestImpl.configLevels, [parameter]: value },
    }));

  const handleConfigLevelsNumberUpdate = (parameter, value) =>
    !isNaN(value) && handleConfigLevelsUpdate(parameter, value);

  const togglePublicInfo = (toggledValue) => {
    let publicToCommunity = test.publicToCommunity;
    let publicToCommunitySolution = test.publicToCommunitySolution;
    if (toggledValue === 'publicToCommunity') {
      publicToCommunity = !publicToCommunity;
      if (!publicToCommunity) publicToCommunitySolution = false;
    }
    if (toggledValue === 'publicToCommunitySolution') {
      publicToCommunitySolution = !publicToCommunitySolution;
      if (publicToCommunitySolution) publicToCommunity = true;
    }
    setTest((oldTest) => ({ ...oldTest, publicToCommunity, publicToCommunitySolution }));
  };

  const toggleIsOfficial = () => {
    setTest((oldTest) => {
      let tags = oldTest.tags || [];
      if (tags.includes(OFFICIAL_TAG)) tags = tags.filter((tag) => tag !== OFFICIAL_TAG);
      else tags.push(OFFICIAL_TAG);
      return { ...oldTest, tags };
    });
  };

  return (
    <CreatorTabContentBackground>
      {showMoveTestModal && <MoveTestToCompanyModal closeModal={() => setShowMoveTestModal(false)} />}
      <FreecodeFlowOverlay />
      <CreatorTabRowContainer>
        <CreatorTabColumnContainer>
          <FreecodeTestMeta />
          <FreecodeLevelSettings />
          {userRole === userRoles.SUPER_ADMIN && (
            <>
              <CreatorSection>
                <SectionHeader>Custom Timeout</SectionHeader>
                <Text>
                  Strongly discouraged. There is no indicator that the solution is still alive, large values might lead
                  to user confusion and dead workloads.
                </Text>
                <Checkbox
                  checked={!!testImpl.configLevels.solutionTimeout}
                  label="I know what I am doing"
                  onChange={() =>
                    handleConfigLevelsUpdate('solutionTimeout', testImpl.configLevels.solutionTimeout ? undefined : 5)
                  }
                />
                {!!testImpl.configLevels.solutionTimeout && (
                  <InputContainer>
                    <Label noWrap>Seconds per level:</Label>
                    <NumberInput
                      value={testImpl.configLevels.solutionTimeout}
                      onChange={(e) => handleConfigLevelsNumberUpdate('solutionTimeout', e.target.value)}
                    ></NumberInput>
                  </InputContainer>
                )}
              </CreatorSection>
              <CreatorSection>
                <RectangleButton small onClick={() => setShowMoveTestModal(true)}>
                  Change Company
                </RectangleButton>
              </CreatorSection>
            </>
          )}
        </CreatorTabColumnContainer>
        <CreatorTabColumnContainer>
          {test.category !== categories.CODE.category && (
            <CreatorSection>
              <SectionHeader>Community</SectionHeader>
              <Text>Public implementations and solutions can be inspected and copied by other users</Text>
              <InputContainer>
                <Checkbox
                  checked={test.publicToCommunity}
                  title="The implementation will be public so others can inspect it and create new ones with the code as a base"
                  label="Public Implementation"
                  onChange={() => togglePublicInfo('publicToCommunity')}
                />
              </InputContainer>
              <InputContainer>
                <Checkbox
                  checked={test.publicToCommunitySolution}
                  title="The creator solution will be public so others can inspect how it is intended to be solved (note: can be used to cheat)"
                  label="Public Solution"
                  onChange={() => togglePublicInfo('publicToCommunitySolution')}
                />
              </InputContainer>
              {userRole === userRoles.SUPER_ADMIN && (
                <InputContainer>
                  <Checkbox
                    checked={test.tags?.includes?.(OFFICIAL_TAG)}
                    title="Official implementations are displayed by default on the index pages"
                    label="Use official tag"
                    onChange={toggleIsOfficial}
                  />
                </InputContainer>
              )}
              <InputContainer>
                <Checkbox
                  checked={testImpl.isFlow}
                  label="Is Flow"
                  onChange={() =>
                    setTestImpl({ ...testImpl, isFlow: !testImpl.isFlow, flow: testImpl?.flow ?? 'Flow text.' })
                  }
                />
              </InputContainer>
              {testImpl.isFlow && (
                <RectangleButton small marginTop onClick={() => setShowFlowOverlay(true)}>
                  Review Flow
                </RectangleButton>
              )}
            </CreatorSection>
          )}
          <FreecodeVersioning />
        </CreatorTabColumnContainer>
      </CreatorTabRowContainer>
    </CreatorTabContentBackground>
  );
}
